import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Route, Routes } from 'react-router-dom';

// Third-party packages.
import Helmet from 'react-helmet';

import * as dayjs from 'dayjs';
import * as isoWeek from 'dayjs/plugin/isoWeek';

// Api.
import EventsApi from '../../../api/instance/EventsApi';

// Types.
import EventType from '../../../types/instance/EventType';
import Event from '../../../types/instance/Event';

// Context.
import { useTimeRange } from '../../../context/TimeRangeContext';

// Constants.
import { DATA_INITIALISING, DATA_LOADING, DATA_LOADED } from '../../../component/Constants';

// Views.
import List from './List';
import Detail from './Detail';
import Breadcrumb from "../../../container/breadcrumb";
import AdminTrail from "../../../container/breadcrumb/AdminTrail";
import HeaderActions from "../../../container/header_actions";
import ConditionalRender from "../../../helpers/ConditionalRender";

// DayJS plugins - note, the position of these in this file are important.
dayjs.extend(isoWeek.default);

interface IEventTypesProps {
    eventTypes: EventType[],
    toggleMenu: Function,
}

function EventTypes(props: IEventTypesProps) {
    const timeRangeContext = useTimeRange();
    const [loading, setLoading] = useState<number>(DATA_INITIALISING);
    const [events, setEvents] = useState<Event[]>([]);

    const getData = async () => {
        setLoading(DATA_LOADING);
        
        // Get all events for all time.
        const eventsData = await EventsApi.getAllEvents(timeRangeContext);

        setLoading(DATA_LOADED);
        setEvents(eventsData || []);
    };

    useEffect(() => {
        if (loading === DATA_INITIALISING) {
            getData();
        }
    }, [loading]);

    const refresh = () => {
        getData();
    };

    return (
        <div id="content">
            <Helmet>
                <title>Event Types - Settings - DBmarlin</title>
                <meta name="description" content="Manage custom event types" />
            </Helmet>

            <div id="top-header-wrapper" className="row row-cols-lg-3 row-cols-sm-1 row-cols-md-2">
                <Breadcrumb heading="Event Types">
                    {[<AdminTrail key="event-types-trail" section="Event Types"
                                  sectionPath="/admin/event-types"
                                  resolveEntityLabel={(id) =>
                                      props.eventTypes.find(eventType =>
                                          eventType.eventTypeId === Number(id))?.title}/>]}
                </Breadcrumb>
                <HeaderActions toggleMenu={props.toggleMenu} refresh={refresh}/>
            </div>

            <div className="loader">
                <ConditionalRender if={false}>
                    <div className="bar"></div>
                </ConditionalRender>
            </div>

            <div className="row row-cols-1">
                <Routes>
                    <Route path="/:actionId" element={<Detail />} />
                    <Route path="/:actionId/:eventId" element={<Detail />} />
                    <Route path="/" element={<List loading={loading} eventTypes={props.eventTypes} events={events} refresh={refresh} />} />
                </Routes>
            </div>
        </div>
    );
}

EventTypes.propTypes = {
    toggleMenu: PropTypes.func.isRequired,
};

export default EventTypes;
